/**
 * Tiny localStorage-backed keystore for the user's pasted Anthropic + Deepgram
 * API keys. The desktop app encrypts these via Electron `safeStorage`; the
 * PWA can't, so we store plaintext. Same risk profile as the desktop today —
 * the user trusts their own device. The Settings UI surfaces this clearly.
 */

const ANTHROPIC_KEY = 'caption.pwa.anthropicKey';
const DEEPGRAM_KEY = 'caption.pwa.deepgramKey';
const NVIDIA_KEY = 'caption.pwa.nvidiaKey';
const GROQ_KEY = 'caption.pwa.groqKey';
const ASSEMBLYAI_KEY = 'caption.pwa.assemblyaiKey';

/**
 * Strip whitespace + invisible Unicode characters that copy/paste sometimes
 * drags in (zero-width space U+200B, zero-width non-joiner U+200C, BOM
 * U+FEFF, etc.). A single stray ZWSP at the end of an API key looks
 * identical to a clean key in any text input but causes the server to
 * reject it as unauthorized.
 */
function sanitizeKey(value: string): string {
  // ​–‍ = zero-width space / non-joiner / joiner.
  // ﻿       = byte-order mark (often pasted from rich-text fields).
  return value.trim().replaceAll(/[​-‍﻿]/g, '');
}

export const keys = {
  getAnthropic(): string | null {
    const raw = localStorage.getItem(ANTHROPIC_KEY);
    if (!raw) return null;
    const clean = sanitizeKey(raw);
    // Heal an entry that was saved before the sanitizer existed.
    if (clean !== raw) localStorage.setItem(ANTHROPIC_KEY, clean);
    return clean || null;
  },
  setAnthropic(value: string): void {
    const clean = sanitizeKey(value);
    if (clean) localStorage.setItem(ANTHROPIC_KEY, clean);
    else localStorage.removeItem(ANTHROPIC_KEY);
  },
  clearAnthropic(): void {
    localStorage.removeItem(ANTHROPIC_KEY);
  },
  getDeepgram(): string | null {
    const raw = localStorage.getItem(DEEPGRAM_KEY);
    if (!raw) return null;
    const clean = sanitizeKey(raw);
    if (clean !== raw) localStorage.setItem(DEEPGRAM_KEY, clean);
    return clean || null;
  },
  setDeepgram(value: string): void {
    const clean = sanitizeKey(value);
    if (clean) localStorage.setItem(DEEPGRAM_KEY, clean);
    else localStorage.removeItem(DEEPGRAM_KEY);
  },
  clearDeepgram(): void {
    localStorage.removeItem(DEEPGRAM_KEY);
  },
  getNvidia(): string | null {
    const raw = localStorage.getItem(NVIDIA_KEY);
    if (!raw) return null;
    const clean = sanitizeKey(raw);
    if (clean !== raw) localStorage.setItem(NVIDIA_KEY, clean);
    return clean || null;
  },
  setNvidia(value: string): void {
    const clean = sanitizeKey(value);
    if (clean) localStorage.setItem(NVIDIA_KEY, clean);
    else localStorage.removeItem(NVIDIA_KEY);
  },
  clearNvidia(): void {
    localStorage.removeItem(NVIDIA_KEY);
  },
  getGroq(): string | null {
    const raw = localStorage.getItem(GROQ_KEY);
    if (!raw) return null;
    const clean = sanitizeKey(raw);
    if (clean !== raw) localStorage.setItem(GROQ_KEY, clean);
    return clean || null;
  },
  setGroq(value: string): void {
    const clean = sanitizeKey(value);
    if (clean) localStorage.setItem(GROQ_KEY, clean);
    else localStorage.removeItem(GROQ_KEY);
  },
  clearGroq(): void {
    localStorage.removeItem(GROQ_KEY);
  },
  getAssemblyAI(): string | null {
    const raw = localStorage.getItem(ASSEMBLYAI_KEY);
    if (!raw) return null;
    const clean = sanitizeKey(raw);
    if (clean !== raw) localStorage.setItem(ASSEMBLYAI_KEY, clean);
    return clean || null;
  },
  setAssemblyAI(value: string): void {
    const clean = sanitizeKey(value);
    if (clean) localStorage.setItem(ASSEMBLYAI_KEY, clean);
    else localStorage.removeItem(ASSEMBLYAI_KEY);
  },
  clearAssemblyAI(): void {
    localStorage.removeItem(ASSEMBLYAI_KEY);
  },
};
